/**
 * The one way a customer document reads an address.
 *
 * The same logical field arrives in TWO shapes depending on which endpoint
 * served it: the public projections format it to a string on the server
 * (`formatServiceAddress`), while the dashboard endpoints return the raw JSON
 * column. A document component cannot know which road its payload took, and
 * assuming one of them is exactly how the invoice PDF crashed with
 * `TypeError: ….trim is not a function` on every office Preview and Download:
 * the type said `string`, the value was an object, and the throw killed the
 * whole PDF.
 *
 * So this accepts both, and callers stop caring.
 */

export interface DocumentAddress {
  street?: string | null;
  /** Some snapshots use `street1`; the billing helpers already read both. */
  street1?: string | null;
  street2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  /** Billing-shaped snapshots spell it `postalCode`. */
  postalCode?: string | null;
  country?: string | null;
}

/** Anything a document may be handed for an address field. */
export type DocumentAddressInput = DocumentAddress | string | null | undefined;

const text = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

/**
 * The address as display lines: street, street2, "City, ST ZIP", country.
 *
 * A string input is returned as a single line, which is what the server-side
 * formatter already produced. An object is read field by field. Anything else
 * (a number, an array, a malformed payload) yields no lines rather than
 * throwing, because a document that cannot print an address must still print.
 */
export function documentAddressLines(address: DocumentAddressInput): string[] {
  if (!address) return [];
  if (typeof address === 'string') {
    const single = address.trim();
    return single ? [single] : [];
  }
  if (typeof address !== 'object') return [];

  const value = address as DocumentAddress;
  const lines: string[] = [];
  const street = text(value.street) || text(value.street1);
  if (street) lines.push(street);
  const street2 = text(value.street2);
  if (street2) lines.push(street2);

  // "City, ST ZIP", the shape the server's own formatter produces for the
  // string payload (`formatServiceAddress` joins state and zip with a space,
  // then city with a comma). Rendering the object path as "City, ST, ZIP"
  // would make the same address look different depending on which endpoint
  // served it, which is the difference this reader exists to erase.
  const stateZip = [text(value.state), text(value.zip) || text(value.postalCode)]
    .filter(Boolean)
    .join(' ');
  const cityLine = [text(value.city), stateZip].filter(Boolean).join(', ');
  if (cityLine) lines.push(cityLine);

  const country = text(value.country);
  if (country) lines.push(country);
  return lines;
}

/** The same address on one line, for links, titles and metadata columns. */
export function documentAddressText(address: DocumentAddressInput): string {
  return documentAddressLines(address).join(', ');
}
